import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import {
  canManageInterconnects,
  interconnectsPluginEnabled,
} from "./interconnectsAccess";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import {
  fetchInterconnectPartnerOptions,
  saveProjectInterconnectFields,
} from "./interconnectsApi";
import {
  InterconnectFieldRow,
  SegmentGroupPicker,
} from "./interconnectFormPickers";
import {
  buildInterconnectSegmentGroupMap,
  expansionProjectHasCdt,
  formatInterconnectAssociatedSegmentLabel,
  formatInterconnectSegmentLabel,
  interconnectFieldsToApiBody,
} from "./interconnectsUtils";
import "./interconnectsPage.css";

function PartnerInput({ value, onChange, options, disabled, placeholder }) {
  const listId = useMemo(() => `cdt-partner-${Math.random().toString(36).slice(2)}`, []);
  return (
    <>
      <input
        type="text"
        className="commercial-field-input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        list={listId}
      />
      <datalist id={listId}>
        {(options || []).map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </>
  );
}

export function CdtInterconnectReadPanel({ interconnectFields, projectRow, segmentGroupsByBase }) {
  if (!interconnectsPluginEnabled() || !expansionProjectHasCdt(projectRow)) return null;
  const fields = interconnectFields?.fields;
  if (!fields) return null;
  const associatedBases = fields.associated_segment_bases || [];
  const linkedProject = interconnectFields?.cdt?.linkedProject;

  return (
    <section className="commercial-section-panel interconnects-embedded-panel">
      <h3 className="commercial-section-title">CDT interconnect</h3>
      <div className="commercial-read-fields">
        <div className="commercial-read-row">
          <span className="commercial-read-label">CDT number</span>
          <span className="commercial-read-value">{fields.cdt_number || "—"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Location</span>
          <span className="commercial-read-value">{fields.location || "—"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">GPS</span>
          <span className="commercial-read-value">{fields.gps || "—"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Interconnecting partner</span>
          <span className="commercial-read-value">{fields.interconnecting_partner || "—"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Install partner</span>
          <span className="commercial-read-value">{fields.install_partner || "—"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Location finalized</span>
          <span className="commercial-read-value">{fields.location_finalized ? "Yes" : "No"}</span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Linked work order</span>
          <span className="commercial-read-value">
            {linkedProject?.id ? (
              <Link to={`/project/${linkedProject.id}`} className="interconnects-link">
                {formatInterconnectSegmentLabel(linkedProject)}
              </Link>
            ) : (
              formatInterconnectSegmentLabel(projectRow)
            )}
          </span>
        </div>
        <div className="commercial-read-row">
          <span className="commercial-read-label">Associated segments</span>
          <span className="commercial-read-value">
            {associatedBases.length ? (
              <ul className="interconnects-read-list">
                {associatedBases.map((base) => (
                  <li key={base}>
                    <Link to={`/expansion/segment/${encodeURIComponent(base)}`} className="interconnects-link">
                      {formatInterconnectAssociatedSegmentLabel(base, segmentGroupsByBase)}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              "—"
            )}
          </span>
        </div>
        {fields._cdtId ? (
          <p className="project-detail-muted interconnects-registry-link">
            <Link to={`/interconnects/${fields._cdtId}`} className="interconnects-link">
              View in Interconnects registry
            </Link>
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default function CdtInterconnectPanel({
  projectRow,
  projectId,
  interconnectFields,
  onInterconnectFieldsChange,
  editing,
  canEdit,
  apiUrl,
  getAuthHeaders,
}) {
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [expansionProjects, setExpansionProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [localFields, setLocalFields] = useState(null);

  const applicable = expansionProjectHasCdt(projectRow);
  const fields = localFields ?? interconnectFields?.fields ?? null;
  const segmentGroupsByBase = useMemo(
    () => buildInterconnectSegmentGroupMap(expansionProjects),
    [expansionProjects]
  );
  const segmentGroupList = useMemo(() => [...segmentGroupsByBase.values()], [segmentGroupsByBase]);

  useEffect(() => {
    setLocalFields(null);
    setSaveError("");
  }, [projectId, interconnectFields]);

  useEffect(() => {
    if (!isPluginEnabled(PLUGIN_IDS.INTERCONNECTS) || !applicable) return undefined;
    let cancelled = false;
    fetchInterconnectPartnerOptions({ apiUrl, getAuthHeaders })
      .then((opts) => {
        if (!cancelled) setPartnerOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setPartnerOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders, applicable]);

  useEffect(() => {
    if (!isPluginEnabled(PLUGIN_IDS.INTERCONNECTS) || !applicable) return undefined;
    let cancelled = false;
    const base = apiUrl || getApiBase();
    axios
      .get(`${base}/projects`, { headers: getAuthHeaders?.() })
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data || []).filter((r) => isCvinExpansion(r));
        rows.sort((a, b) => String(a.work_order || "").localeCompare(String(b.work_order || "")));
        setExpansionProjects(rows);
      })
      .catch(() => {
        if (!cancelled) setExpansionProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders, applicable]);

  const setField = useCallback((key, value) => {
    setLocalFields((prev) => {
      const base = prev ?? interconnectFields?.fields ?? {};
      return { ...base, [key]: value };
    });
  }, [interconnectFields]);

  const handleSave = useCallback(async () => {
    if (!fields || !canEdit) return;
    setSaving(true);
    setSaveError("");
    try {
      const result = await saveProjectInterconnectFields(
        projectId,
        interconnectFieldsToApiBody(fields),
        { apiUrl, getAuthHeaders }
      );
      setLocalFields(null);
      onInterconnectFieldsChange?.(result);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [fields, canEdit, projectId, apiUrl, getAuthHeaders, onInterconnectFieldsChange]);

  if (!interconnectsPluginEnabled() || !applicable) return null;
  if (!fields) return null;

  if (!editing) {
    return (
      <CdtInterconnectReadPanel
        interconnectFields={interconnectFields}
        projectRow={projectRow}
        segmentGroupsByBase={segmentGroupsByBase}
      />
    );
  }

  const viewerCanManage = canManageInterconnects(getViewerEmailFromStorage());
  const disabled = !canEdit || !viewerCanManage;
  const linkedProject = interconnectFields?.cdt?.linkedProject;

  return (
    <section className="commercial-section-panel interconnects-embedded-panel">
      <h3 className="commercial-section-title">CDT interconnect</h3>
      <div className="commercial-fields interconnects-form-fields">
        <InterconnectFieldRow label="CDT number" hint="Extracted from project title when empty.">
          <input
            type="text"
            className="commercial-field-input"
            value={fields.cdt_number ?? ""}
            onChange={(e) => setField("cdt_number", e.target.value)}
            disabled={disabled}
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="Location">
          <input
            type="text"
            className="commercial-field-input"
            value={fields.location ?? ""}
            onChange={(e) => setField("location", e.target.value)}
            disabled={disabled}
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="GPS" hint="Latitude, longitude (e.g. 37.7749, -122.4194)">
          <input
            type="text"
            className="commercial-field-input"
            value={fields.gps ?? ""}
            onChange={(e) => setField("gps", e.target.value)}
            disabled={disabled}
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="Interconnecting partner">
          <PartnerInput
            value={fields.interconnecting_partner ?? ""}
            onChange={(v) => setField("interconnecting_partner", v)}
            options={partnerOptions}
            disabled={disabled}
            placeholder="Partner name"
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="Install partner">
          <PartnerInput
            value={fields.install_partner ?? ""}
            onChange={(v) => setField("install_partner", v)}
            options={partnerOptions}
            disabled={disabled}
            placeholder="Partner name"
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="Linked work order" hint="This interconnect is linked to the current subsegment.">
          <span className="commercial-field-input interconnects-linked-wo-readonly">
            {formatInterconnectSegmentLabel(linkedProject || projectRow)}
          </span>
        </InterconnectFieldRow>
        <InterconnectFieldRow
          label="Associated segments"
          hint="Parent expansion segments related to this interconnect (not subsegments)."
        >
          <SegmentGroupPicker
            segmentGroups={segmentGroupList}
            selectedBases={fields.associated_segment_bases || []}
            onChange={(bases) => setField("associated_segment_bases", bases)}
            disabled={disabled}
          />
        </InterconnectFieldRow>
        <InterconnectFieldRow label="Location finalized">
          <label className="commercial-multiselect-option">
            <input
              type="checkbox"
              checked={Boolean(fields.location_finalized)}
              onChange={(e) => setField("location_finalized", e.target.checked)}
              disabled={disabled}
            />
            Location is finalized
          </label>
        </InterconnectFieldRow>
        {fields._cdtId ? (
          <p className="project-detail-muted interconnects-registry-link">
            <Link to={`/interconnects/${fields._cdtId}`} className="interconnects-link">
              Open in Interconnects registry
            </Link>
          </p>
        ) : null}
        {!disabled ? (
          <div className="interconnects-form-actions">
            <button
              type="button"
              className="interconnects-btn interconnects-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save interconnect fields"}
            </button>
            {saveError ? <span className="project-detail-error">{saveError}</span> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
